/**
 * Metamagic Options for Sorcerers (SRD)
 *
 * Sorcerers can use Sorcery Points to modify their spells with Metamagic.
 * They choose 2 options at level 3, and gain additional options at levels 10 and 17.
 */

export interface MetamagicOption {
  id: string;
  name: string;
  description: string;
  cost: string; // Sorcery point cost description
}

export const METAMAGIC_OPTIONS: MetamagicOption[] = [
  {
    id: 'careful-spell',
    name: 'Careful Spell',
    description:
      'When you cast a spell that forces other creatures to make a saving throw, you can protect some of those creatures from the spell\'s full force. Choose a number of creatures up to your Charisma modifier (minimum 1). A chosen creature automatically succeeds on its saving throw against the spell.',
    cost: '1 sorcery point',
  },
  {
    id: 'distant-spell',
    name: 'Distant Spell',
    description:
      'When you cast a spell that has a range of 5 feet or greater, you can double the range of the spell. When you cast a spell that has a range of touch, you can make the range of the spell 30 feet.',
    cost: '1 sorcery point',
  },
  {
    id: 'empowered-spell',
    name: 'Empowered Spell',
    description:
      'When you roll damage for a spell, you can reroll a number of the damage dice up to your Charisma modifier (minimum 1). You must use the new rolls. You can use Empowered Spell even if you have already used a different Metamagic option during the casting of the spell.',
    cost: '1 sorcery point',
  },
  {
    id: 'extended-spell',
    name: 'Extended Spell',
    description:
      'When you cast a spell that has a duration of 1 minute or longer, you can double its duration, to a maximum duration of 24 hours.',
    cost: '1 sorcery point',
  },
  {
    id: 'heightened-spell',
    name: 'Heightened Spell',
    description:
      'When you cast a spell that forces a creature to make a saving throw to resist its effects, you can give one target of the spell disadvantage on its first saving throw made against the spell.',
    cost: '3 sorcery points',
  },
  {
    id: 'quickened-spell',
    name: 'Quickened Spell',
    description:
      'When you cast a spell that has a casting time of 1 action, you can change the casting time to 1 bonus action for this casting.',
    cost: '2 sorcery points',
  },
  {
    id: 'subtle-spell',
    name: 'Subtle Spell',
    description:
      'When you cast a spell, you can cast it without any somatic or verbal components.',
    cost: '1 sorcery point',
  },
  {
    id: 'twinned-spell',
    name: 'Twinned Spell',
    description:
      'When you cast a spell that targets only one creature and doesn\'t have a range of self, you can target a second creature in range with the same spell. To be eligible, a spell must be incapable of targeting more than one creature at the spell\'s current level.',
    cost: 'sorcery points equal to spell level (1 for cantrips)',
  },
];

/**
 * Levels at which Sorcerers can choose new Metamagic options
 * Level 3: Choose 2
 * Level 10: Choose 1 more
 * Level 17: Choose 1 more
 */
export const METAMAGIC_LEVELS = [3, 10, 17];

/**
 * Get number of metamagic options allowed for a given sorcerer level
 */
export function getMetamagicCount(sorcererLevel: number): number {
  if (sorcererLevel < 3) return 0;
  if (sorcererLevel < 10) return 2;
  if (sorcererLevel < 17) return 3;
  return 4;
}

export function getMetamagicOption(id: string): MetamagicOption | undefined {
  return METAMAGIC_OPTIONS.find((m) => m.id === id);
}

export function getMetamagicOptionIds(): string[] {
  return METAMAGIC_OPTIONS.map((m) => m.id);
}
